import type { ChangeEvent } from "react";
import {
  CatalogChipInput,
  CatalogFormSelect,
  CatalogFormTextField,
} from "./FormControls";
import type {
  FormInputChangeEvent,
  CatalogSelectChangeEvent,
  LLMConfigFormData,
  ModelFieldConfig,
} from "../types";

type ModelSpecificFieldsProps = {
  activeModelType: string | null;
  fieldsByType: Record<string, ModelFieldConfig[]>;
  formData: LLMConfigFormData;
  formErrors?: Partial<Record<keyof LLMConfigFormData, string>>;
  disabled?: boolean;
  onInputChange: (event: FormInputChangeEvent) => void;
  onChipChange: (
    field: keyof LLMConfigFormData,
  ) => (event: React.SyntheticEvent, newValue: string[]) => void;
};

const ModelSpecificFields = ({
  activeModelType,
  fieldsByType,
  formData,
  formErrors = {},
  disabled,
  onInputChange,
  onChipChange,
}: ModelSpecificFieldsProps) => {
  if (!activeModelType) return null;

  const fields = fieldsByType[activeModelType] || [];

  return fields.map((field) => {
    if (field.type === "chip") {
      if (field.maxItems === 1) {
        const value = (formData[field.key] as string[])[0] || "";
        const handleSingleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
          onChipChange(field.key)(
            event,
            event.target.value ? [event.target.value] : [],
          );
        };

        return (
          <CatalogFormTextField
            key={field.key}
            label={field.label}
            name={field.key}
            value={value}
            onChange={handleSingleValueChange}
            disabled={disabled}
            placeholder={field.placeholder}
            grid={field.grid}
            testId={`ai-catalog-form-input-${field.key}`}
            error={Boolean(formErrors[field.key])}
            helperText={formErrors[field.key] || field.helperText}
          />
        );
      }

      return (
        <CatalogChipInput
          key={field.key}
          label={field.label}
          value={formData[field.key] as string[]}
          onChange={onChipChange(field.key)}
          placeholder={field.placeholder}
          grid={field.grid}
          testId={`ai-catalog-form-chip-input-${field.key}`}
          error={Boolean(formErrors[field.key])}
          helperText={formErrors[field.key]}
          disabled={disabled}
        />
      );
    }

    if (field.type === "select") {
      const fieldValue = formData[field.key];
      const value = Array.isArray(fieldValue)
        ? fieldValue[0] || ""
        : (fieldValue as string);
      const handleSelectChange = (event: CatalogSelectChangeEvent) => {
        if (Array.isArray(fieldValue)) {
          onChipChange(field.key)(
            event as unknown as React.SyntheticEvent,
            event.target.value ? [event.target.value] : [],
          );
          return;
        }

        onInputChange(event);
      };

      return (
        <CatalogFormSelect
          key={field.key}
          label={field.label}
          name={field.key}
          value={value}
          onChange={handleSelectChange}
          disabled={disabled}
          placeholder={field.placeholder}
          grid={field.grid}
          options={field.options}
          allowEmpty={field.allowEmpty}
          testId={`ai-catalog-form-select-${field.key}`}
          error={Boolean(formErrors[field.key])}
          helperText={formErrors[field.key] || field.helperText}
        />
      );
    }

    return (
      <CatalogFormTextField
        key={field.key}
        label={field.label}
        name={field.key}
        value={formData[field.key] as string | number}
        onChange={onInputChange}
        type={field.inputType}
        inputProps={field.inputProps}
        disabled={disabled}
        placeholder={field.placeholder}
        required={field.required}
        suffix={field.suffix}
        grid={field.grid}
        testId={`ai-catalog-form-input-${field.key}`}
        error={Boolean(formErrors[field.key])}
        helperText={formErrors[field.key] || field.helperText}
      />
    );
  });
};

export default ModelSpecificFields;
